// Drawn-to-scale visuals for the dose result: oral syringe, split tablets, and the day's dose times.
// All colours come from theme tokens so they follow light/dark mode.
import { useId } from 'react';
import { formatFraction, formatNumber, type FrequencyKey } from '@/lib/pedsDose';
import { cn } from '@/lib/utils';

// Standard oral syringe sizes with their printed graduations
const SYRINGES = [
  { cap: 1,   minor: 0.05, label: 0.2 },
  { cap: 2.5, minor: 0.1,  label: 0.5 },
  { cap: 5,   minor: 0.2,  label: 1 },
  { cap: 10,  minor: 0.5,  label: 2 },
  { cap: 20,  minor: 1,    label: 5 },
];

const EASE = 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1), width 520ms cubic-bezier(0.22, 1, 0.36, 1)';

/** Smallest standard oral syringe that holds the dose (20 ml, refilled, above that). */
export function pickSyringe(ml: number) {
  const s = SYRINGES.find(x => ml <= x.cap + 1e-9) ?? SYRINGES[SYRINGES.length - 1];
  return { ...s, fills: Math.max(1, Math.ceil(ml / s.cap - 1e-9)) };
}

export function SyringeGauge({ ml, idle = false }: { ml: number; idle?: boolean }) {
  const fadeId = `rod-fade-${useId().replace(/:/g, '')}`;
  const syringe = pickSyringe(idle ? 5 : ml);
  const { cap, minor, label, fills } = syringe;
  const perFill = idle ? 0 : fills > 1 ? cap : ml;
  const X0 = 40, X1 = 272, W = X1 - X0;        // barrel, 0 ml at the nozzle end
  const frac = Math.min(1, perFill / cap);
  const ticks: { x: number; major: boolean; v: number }[] = [];
  const steps = Math.round(cap / minor);
  for (let i = 0; i <= steps; i++) {
    const v = i * minor;
    const major = Math.abs(v / label - Math.round(v / label)) < 1e-6;
    ticks.push({ x: X0 + (v / cap) * W, major, v });
  }

  return (
    <figure className="space-y-1">
      <svg viewBox="0 0 348 60" className="w-full h-auto" role="img"
        aria-label={idle ? 'Oral syringe' : `Draw to ${formatNumber(perFill)} ml on a ${cap} ml oral syringe`}>
        <defs>
          {/* The rod is drawn as a stub that fades out — a full-length rod would not fit once drawn */}
          <linearGradient id={fadeId} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.4" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* tip + nozzle */}
        <rect x="14" y="27" width="10" height="6" rx="1.5" className="fill-muted-foreground/30" />
        <rect x="24" y="24" width="16" height="12" rx="2" className="fill-background stroke-foreground/35" strokeWidth="1.2" />
        {/* liquid */}
        <rect x={X0} y="17" height="26" className="fill-primary/25"
          style={{ width: `${frac * W}px`, transition: EASE }} />
        {/* barrel */}
        <rect x={X0} y="16" width={W} height="28" rx="3" className="fill-none stroke-foreground/45" strokeWidth="1.4" />
        {/* graduations */}
        {ticks.map(t => (
          <line key={t.x} x1={t.x} x2={t.x} y1="16" y2={t.major ? 25 : 21}
            className={t.major ? 'stroke-foreground/60' : 'stroke-foreground/30'} strokeWidth={t.major ? 1.1 : 0.8} />
        ))}
        {ticks.filter(t => t.major).map(t => (
          <text key={`l${t.x}`} x={t.x} y="11" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 8 }}>
            {formatNumber(t.v)}
          </text>
        ))}
        {/* flange */}
        {/* starts below the scale labels so the last label ("2.5", "5"…) stays readable */}
        <rect x={X1 + 1} y="13" width="5" height="41" rx="2" className="fill-muted-foreground/25" />
        {/* plunger: stopper sits at the dose line, rod slides out with it */}
        <g style={{ transform: `translateX(${frac * W}px)`, transition: EASE }}>
          <rect x={X0 - 1} y="17.5" width="7" height="25" rx="1.5" className="fill-foreground/70" />
          <rect x={X0 + 6} y="27" width="70" height="6" rx="1" fill={`url(#${fadeId})`} className="text-muted-foreground" />
          {!idle && <line x1={X0} x2={X0} y1="12" y2="48" className="stroke-primary" strokeWidth="1.2" strokeDasharray="2 2" />}
        </g>
      </svg>
      {!idle && (
        <figcaption className="text-2xs text-muted-foreground">
          Draw to <span className="font-semibold text-foreground tabular-nums">{formatNumber(fills > 1 ? ml : perFill)} ml</span> on a {cap} ml oral syringe
          {fills > 1 && <> — fill it {fills} times ({formatNumber(ml / fills)} ml each)</>}
        </figcaption>
      )}
    </figure>
  );
}

// Pie wedge for a partial tablet, starting at 12 o'clock
function wedge(cx: number, cy: number, r: number, fraction: number) {
  const a = fraction * 2 * Math.PI - Math.PI / 2;
  const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${fraction > 0.5 ? 1 : 0} 1 ${x} ${y} Z`;
}

export function TabletGlyphs({ units, capsule = false }: { units: number; capsule?: boolean }) {
  const whole = Math.floor(units + 1e-9);
  const part = Math.round((units - whole) * 4) / 4;
  const shown = Math.min(whole + (part > 0 ? 1 : 0), 4);
  const extra = whole + (part > 0 ? 1 : 0) - shown;
  const name = capsule ? 'capsule' : 'tablet';
  return (
    <figure className="space-y-1">
      <div className="flex items-center gap-2" role="img" aria-label={`${formatFraction(units)} ${name}${units > 1 ? 's' : ''}`}>
        {Array.from({ length: shown }, (_, i) => {
          const fraction = i < whole ? 1 : part;
          return capsule ? (
            <svg key={i} viewBox="0 0 56 28" className="h-7 w-14">
              <rect x="2" y="3" width="52" height="22" rx="11" className="fill-primary/25 stroke-primary" strokeWidth="1.5" />
              <line x1="28" x2="28" y1="3" y2="25" className="stroke-primary/60" strokeWidth="1.2" />
            </svg>
          ) : (
            <svg key={i} viewBox="0 0 36 36" className="h-9 w-9">
              <circle cx="18" cy="18" r="15" className="fill-none stroke-foreground/35" strokeWidth="1.3" strokeDasharray={fraction < 1 ? '3 2.5' : undefined} />
              {fraction >= 1
                ? <circle cx="18" cy="18" r="15" className="fill-primary/25 stroke-primary" strokeWidth="1.5" />
                : <path d={wedge(18, 18, 15, fraction)} className="fill-primary/30 stroke-primary" strokeWidth="1.5" strokeLinejoin="round" />}
              <line x1="18" x2="18" y1="4" y2="32" className="stroke-foreground/20" strokeWidth="1" />
              <line x1="4" x2="32" y1="18" y2="18" className="stroke-foreground/20" strokeWidth="1" />
            </svg>
          );
        })}
        {extra > 0 && <span className="text-xs text-muted-foreground">+{extra}</span>}
      </div>
      <figcaption className="text-2xs text-muted-foreground">
        Give <span className="font-semibold text-foreground">{formatFraction(units)} {name}{units > 1 ? 's' : ''}</span> per dose
        {part > 0 && !capsule && ' — split along the score'}
      </figcaption>
    </figure>
  );
}

// Typical dosing times for each frequency (hour of day)
const DOSE_HOURS: Record<Exclude<FrequencyKey, 'SOS'>, number[]> = {
  OD:  [8],
  BD:  [8, 20],
  TDS: [8, 14, 20],
  QID: [7, 12, 17, 22],
  Q4H: [2, 6, 10, 14, 18, 22],
};

const hourLabel = (h: number) => (h === 0 || h === 24 ? '12a' : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`);

export function DoseDay({ frequency, gapHours, maxPerDay, className }: {
  frequency: FrequencyKey; gapHours: number; maxPerDay: number; className?: string;
}) {
  const sos = frequency === 'SOS';
  const hours = sos ? [] : DOSE_HOURS[frequency];
  const x = (h: number) => 8 + (h / 24) * 284;
  return (
    <figure className={cn('space-y-0.5', className)}>
      <svg viewBox="0 0 300 30" className="w-full h-auto" role="img"
        aria-label={sos ? `As needed, at least ${gapHours} hours apart, at most ${maxPerDay} a day` : `Doses at ${hours.map(hourLabel).join(', ')}`}>
        {/* night shading: 10p–6a */}
        <rect x={x(0)} y="8" width={x(6) - x(0)} height="8" rx="4" className="fill-muted-foreground/10" />
        <rect x={x(22)} y="8" width={x(24) - x(22)} height="8" rx="4" className="fill-muted-foreground/10" />
        <line x1={x(0)} x2={x(24)} y1="12" y2="12" className="stroke-foreground/25" strokeWidth="1.2" strokeDasharray={sos ? '3 3' : undefined} />
        {[0, 6, 12, 18, 24].map(h => (
          <text key={h} x={x(h)} y="28" textAnchor="middle" className="fill-muted-foreground/70" style={{ fontSize: 7.5 }}>{hourLabel(h)}</text>
        ))}
        {hours.map((h, i) => (
          <circle key={h} cx={x(h)} cy="12" r="4.5" className="fill-primary stroke-background animate-in zoom-in-50 fade-in-0 fill-mode-both"
            strokeWidth="2" style={{ animationDelay: `${i * 60}ms`, transformOrigin: `${x(h)}px 12px`, transformBox: 'view-box' }} />
        ))}
      </svg>
      {sos && (
        <figcaption className="text-2xs text-muted-foreground">
          As needed · at least {gapHours} h apart · max {maxPerDay} in 24 h
        </figcaption>
      )}
    </figure>
  );
}
